<template>
    <v-container fluid class="mc-page secondary-font">
        <div class="mc-header">
            <span class="mc-header__icon custom-orange">
                <v-icon color="white">mdi-calendar-sync-outline</v-icon>
            </span>
            <h1 class="mc-header__title">Τα αιτήματά μου</h1>
            <span v-if="team" class="mc-header__team">
                <img v-if="team.logo" :src="`${assetsUrl}/${team.logo}`" :alt="team.name" :title="team.name">
                <span class="mc-header__team-name">{{ team.name }}</span>
            </span>
        </div>

        <!-- no team picked: the requests are of the team of the profile -->
        <div v-if="!team" class="mc-intro">
            <v-icon small color="orange darken-2">mdi-information-outline</v-icon>
            <span>
                Διάλεξε την ομάδα σου στο <router-link :to="{ name: 'Profile' }">προφίλ</router-link>
                για να δεις τα αιτήματα αναβολής και αλλαγής ώρας των αγώνων της.
            </span>
        </div>

        <template v-else>
            <!-- the captain makes the requests from here -->
            <captain-request-panel v-if="isCaptain" ref="panel" class="mt-4" @requested="onRequested" />

            <div class="mc-types">
                <button
                    v-for="item in types"
                    :key="item.value"
                    type="button"
                    class="mc-type"
                    :class="{ 'mc-type--active': type === item.value }"
                    @click="setType(item.value)"
                >
                    <v-icon small>{{ item.icon }}</v-icon>
                    <span>{{ item.label }}</span>
                </button>
            </div>

            <p class="mc-intro">
                <v-icon small color="orange darken-2">mdi-information-outline</v-icon>
                <span v-if="type === 'changes'">Οι αλλαγές ώρας που αφορούν αγώνες της ομάδας σου: ο αγώνας ανταλλάσσει ημέρα, ώρα και γήπεδο με έναν άλλον αγώνα
                    όταν συμφωνήσουν οι αρχηγοί όλων των ομάδων των δύο αγώνων και εγκρίνει η διοργάνωση.</span>
                <span v-else>Οι αναβολές που αφορούν αγώνες της ομάδας σου: ο αγώνας αναβάλλεται όταν εγκρίνει το αίτημα η διοργάνωση.</span>
            </p>

            <div class="mc-filters">
                <button
                    v-for="item in filters"
                    :key="item.value"
                    type="button"
                    class="mc-filter"
                    :class="{ 'mc-filter--active': filter === item.value }"
                    @click="filter = item.value"
                >
                    {{ item.label }}<span class="mc-filter__count">{{ item.count }}</span>
                </button>
            </div>

            <!-- match changes waiting for the vote of the captain -->
            <template v-if="type === 'changes' && awaitingVote.length">
                <div class="mc-section-title">Περιμένουν την ψήφο σας</div>
                <div class="mc-grid mb-6">
                    <match-change-card
                        v-for="request in awaitingVote"
                        :key="`vote-${request.id}`"
                        :request="request"
                        :highlight="request.id === highlightId"
                        @updated="onUpdated"
                    />
                </div>
            </template>

            <div v-if="loading" class="text-center py-8">
                <v-progress-circular indeterminate color="orange" />
            </div>
            <div v-else-if="!visibleRequests.length" class="mc-empty">
                {{ filter === 'open' ? 'Δεν υπάρχουν ανοιχτά αιτήματα' : 'Δεν υπάρχουν αιτήματα' }}
            </div>
            <div v-else-if="type === 'changes'" class="mc-grid">
                <match-change-card
                    v-for="request in visibleRequests"
                    :id="`match-change-${request.id}`"
                    :key="request.id"
                    :request="request"
                    :highlight="request.id === highlightId"
                    @updated="onUpdated"
                />
            </div>
            <div v-else class="mc-grid">
                <postponement-card v-for="request in visibleRequests" :key="request.id" :request="request" @updated="onPostponementUpdated" />
            </div>
        </template>
    </v-container>
</template>

<script>
    import MatchChangeCard from '@/components/MatchChangeCard'
    import PostponementCard from '@/components/PostponementCard'
    import CaptainRequestPanel from '@/components/CaptainRequestPanel'
    import matchChangeService from '@/services/matchChange'
    import postponementService from '@/services/postponement'

    const OPEN = { changes: ['voting', 'pending'], postponements: ['pending'] }

    // The match change and postponement requests about the matches of the team of the profile. The captain makes new
    // requests and votes on the match changes here. ?type=postponements opens the postponements, ?id= highlights a match change.
    export default {
        name: 'MatchChanges',
        components: { MatchChangeCard, PostponementCard, CaptainRequestPanel },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                changes: [],
                postponements: [],
                loading: false,
                filter: 'open',
                types: [
                    { value: 'changes', label: 'Αλλαγές ώρας', icon: 'mdi-swap-horizontal' },
                    { value: 'postponements', label: 'Αναβολές', icon: 'mdi-calendar-remove-outline' }
                ]
            }
        },
        computed: {
            type() {
                return this.$route.query.type === 'postponements' ? 'postponements' : 'changes'
            },
            team() {
                const team = this.$store.getters.getTeam
                return team && team.id ? team : null
            },
            isCaptain() {
                return this.$store.getters.getCaptain
            },
            highlightId() {
                return this.type === 'changes' && this.$route.query.id ? Number(this.$route.query.id) : null
            },
            // only the requests about the matches of my team
            requests() {
                return (this.type === 'changes' ? this.changes : this.postponements).filter(this.isOfMyTeam)
            },
            isOpen() {
                return request => OPEN[this.type].includes(request.status)
            },
            isOfMyTeam() {
                if (!this.team) return () => false
                const teamId = this.team.id
                return this.type === 'changes'
                    ? request => request.votes.some(vote => vote.team.id === teamId)
                    : request => request.team1Id === teamId || request.team2Id === teamId
            },
            // the captain of a team that has not voted yet
            awaitingVote() {
                if (!this.$store.getters.getCaptain || !this.team) return []
                return this.changes.filter(request => request.status === 'voting' &&
                    request.votes.some(vote => vote.team.id === this.team.id && vote.approved === null))
            },
            filters() {
                return [
                    { value: 'open', label: 'Ανοιχτά', count: this.requests.filter(this.isOpen).length },
                    { value: 'all', label: 'Όλα', count: this.requests.length }
                ]
            },
            visibleRequests() {
                const awaiting = this.type !== 'changes' ? [] : this.awaitingVote.map(request => request.id)
                return this.requests
                    .filter(request => !awaiting.includes(request.id))
                    .filter(request => this.filter !== 'open' || this.isOpen(request))
            }
        },
        watch: {
            // e.g. a match change request made from the panel opens it here
            '$route.query.id'() {
                this.fetch()
            }
        },
        created() {
            this.fetch()
        },
        methods: {
            setType(type) {
                if (type === this.type) return
                this.filter = 'open'
                this.$router.replace({ name: 'MatchChanges', query: type === 'postponements' ? { type } : {} })
            },
            fetch() {
                this.loading = true
                Promise.all([
                    matchChangeService.getMatchChanges().then(resp => (resp && resp.data) || []).catch(() => []),
                    postponementService.getSeasonPostponements().then(resp => (resp && resp.data) || []).catch(() => [])
                ]).then(([changes, postponements]) => {
                    this.changes = changes
                    this.postponements = postponements
                    // a link to a closed request (e.g. from a notification) shows it
                    const linked = this.highlightId && this.changes.find(request => request.id === this.highlightId)
                    if (linked && !OPEN.changes.includes(linked.status)) {
                        this.filter = 'all'
                    }
                    this.$nextTick(this.scrollToHighlight)
                }).finally(() => {
                    this.loading = false
                })
            },
            scrollToHighlight() {
                if (!this.highlightId) return
                const element = document.getElementById(`match-change-${this.highlightId}`)
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }
            },
            onRequested() {
                this.fetch()
            },
            onUpdated(request) {
                this.changes = this.changes.map(item => item.id === request.id ? request : item)
            },
            // e.g. cancelled: the team gets the postponement back
            onPostponementUpdated(request) {
                this.postponements = this.postponements.map(item => item.id === request.id ? request : item)
                if (this.$refs.panel) {
                    this.$refs.panel.fetchPostponements()
                }
            }
        }
    }
</script>

<style scoped>
.mc-page {
    max-width: 1100px;
    margin: 0 auto;
}

/* same header as the other list pages (e.g. the penalties) */
.mc-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 18px;
    background: #fff;
    border-radius: 12px;
    border-top: 4px solid #ff7133;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
}

.mc-header__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 10px;
    flex: 0 0 auto;
}

.mc-header__team {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.7);
}

.mc-header__team img {
    width: 28px;
    height: 28px;
    object-fit: contain;
}

.mc-header__title {
    flex: 1 1 auto;
    font-size: 1.9rem;
    font-weight: 800;
    line-height: 1.2;
    text-align: left;
}

/* on its own panel: the page background is a busy image */
.mc-intro {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 16px 0 0;
    padding: 12px 16px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    font-size: 0.9rem;
    line-height: 1.5;
    color: rgba(0, 0, 0, 0.75);
    text-align: left;
}

.mc-intro .v-icon {
    flex: 0 0 auto;
    margin-top: 1px;
}

.mc-types {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 4px;
    margin-top: 16px;
    padding: 4px;
    max-width: 420px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.mc-type {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 12px;
    border-radius: 9px;
    font-weight: 800;
    font-size: 0.9rem;
    color: rgba(0, 0, 0, 0.6);
}

.mc-type .v-icon {
    color: inherit !important;
}

.mc-type--active {
    background: linear-gradient(135deg, #ff7133, #ef6c00);
    color: #fff;
}

.mc-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 8px 0 20px;
}

.mc-filter {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 999px;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.12);
    font-weight: 700;
    font-size: 0.85rem;
}

.mc-filter--active {
    background: #212121;
    border-color: #212121;
    color: #fff;
}

.mc-filter__count {
    padding: 0 6px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.08);
    font-size: 0.72rem;
}

.mc-filter--active .mc-filter__count {
    background: rgba(255, 255, 255, 0.2);
}

.mc-section-title {
    margin-bottom: 10px;
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #ef6c00;
    text-align: left;
}

.mc-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 16px;
}

.mc-empty {
    padding: 24px 16px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    color: rgba(0, 0, 0, 0.55);
}

@media (max-width: 600px) {
    .mc-header__title {
        font-size: 1.4rem;
    }

    /* the logo is enough on a phone */
    .mc-header__team-name {
        display: none;
    }
}

@media (max-width: 400px) {
    .mc-grid {
        grid-template-columns: minmax(0, 1fr);
    }
}
</style>
